import { useState } from "react";
import { Send, CheckCircle, Phone, MessageCircle, Mail } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    school_name: "",
    phone: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Submit to Netlify Forms
      const formData = new URLSearchParams();
      formData.append("form-name", "contact");
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("school_name", form.school_name);
      formData.append("phone", form.phone);
      formData.append("message", form.message);

      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });

      // Also submit to backend API
      try {
        await axios.post(`${API}/contact`, form);
      } catch (backendErr) {
        // Netlify form already captured it, backend is optional
      }

      setSubmitted(true);
    } catch (err) {
      // Fallback: try backend only
      try {
        await axios.post(`${API}/contact`, form);
        setSubmitted(true);
      } catch (backendErr) {
        console.error("Contact submission error:", backendErr);
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section id="contact" data-testid="contact-section" className="bg-white py-24 lg:py-32">
        <div className="max-w-xl mx-auto px-6 text-center">
          <div className="bg-gray-50 border-2 border-gray-100 p-12">
            <CheckCircle size={48} className="text-emerald-500 mx-auto mb-6" strokeWidth={3} />
            <h3
              className="text-2xl font-black text-[#0A0A0A] mb-4 uppercase"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Proposal Request Received!
            </h3>
            <p className="text-gray-500 font-medium" style={{ fontFamily: "'Satoshi', sans-serif" }}>
              Thank you for reaching out. Our team will contact you within 24 hours to discuss your school's needs.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-white py-24 lg:py-32 overflow-hidden">
      <div className="blob-pink" style={{ top: '20%', right: '-5%' }} />
      <div className="blob-blue" style={{ bottom: '-10%', left: '5%' }} />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-[#0A0A0A] mb-6 leading-[0.95]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="contact-heading"
            >
              Request A Custom
              <br />
              Proposal
            </h2>
            <p
              className="text-base text-gray-500 leading-relaxed mb-8 max-w-md font-medium"
              style={{ fontFamily: "'Satoshi', sans-serif" }}
            >
              Every school is different. Tell us about yours and we'll create a
              custom proposal with pricing built specifically for your needs.
            </p>

            <div className="space-y-4 mb-10">
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 bg-emerald-500" />
                <span className="text-sm text-gray-500 font-bold" style={{ fontFamily: "'Satoshi', sans-serif" }}>Free school visit & assessment</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 bg-emerald-500" />
                <span className="text-sm text-gray-500 font-bold" style={{ fontFamily: "'Satoshi', sans-serif" }}>Custom pricing — no two schools pay the same</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2.5 h-2.5 bg-emerald-500" />
                <span className="text-sm text-gray-500 font-bold" style={{ fontFamily: "'Satoshi', sans-serif" }}>Free on-site setup, data migration & training</span>
              </div>
            </div>

            {/* Contact details */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-100 flex items-center justify-center">
                  <Phone size={18} className="text-[#0A0A0A]" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-bold" style={{ fontFamily: "'Satoshi', sans-serif" }}>Phone</div>
                  <a href="tel:+233531988775" className="text-[#0A0A0A] font-bold hover:underline" style={{ fontFamily: "'Satoshi', sans-serif" }}>0531 988 775</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-100 flex items-center justify-center">
                  <MessageCircle size={18} className="text-[#0A0A0A]" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-bold" style={{ fontFamily: "'Satoshi', sans-serif" }}>WhatsApp</div>
                  <a href="https://wa.me/233505066691?text=Hi%20VelTech%2C%20I%20found%20your%20site%20and%20I%20need%20a%20custom%20proposal%20for%20my%20school." className="text-[#0A0A0A] font-bold hover:underline" style={{ fontFamily: "'Satoshi', sans-serif" }}>0505 066 691</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gray-100 flex items-center justify-center">
                  <Mail size={18} className="text-[#0A0A0A]" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-bold" style={{ fontFamily: "'Satoshi', sans-serif" }}>Email</div>
                  <a href="mailto:info@veltechgh.com" className="text-[#0A0A0A] font-bold hover:underline" style={{ fontFamily: "'Satoshi', sans-serif" }}>info@veltechgh.com</a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div>
            <form
              name="contact"
              method="POST"
              data-netlify="true"
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="bg-gray-50 border-2 border-gray-100 p-8 space-y-5"
            >
              <input type="hidden" name="form-name" value="contact" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2 font-black" style={{ fontFamily: "'Satoshi', sans-serif" }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    data-testid="contact-input-name"
                    className="w-full bg-white border-2 border-gray-200 text-[#0A0A0A] p-3.5 focus:ring-2 focus:ring-[#0A0A0A] focus:border-[#0A0A0A] outline-none text-sm font-medium transition-all"
                    style={{ fontFamily: "'Satoshi', sans-serif" }}
                    placeholder="John Mensah"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2 font-black" style={{ fontFamily: "'Satoshi', sans-serif" }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    data-testid="contact-input-email"
                    className="w-full bg-white border-2 border-gray-200 text-[#0A0A0A] p-3.5 focus:ring-2 focus:ring-[#0A0A0A] focus:border-[#0A0A0A] outline-none text-sm font-medium transition-all"
                    style={{ fontFamily: "'Satoshi', sans-serif" }}
                    placeholder="john@school.edu.gh"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2 font-black" style={{ fontFamily: "'Satoshi', sans-serif" }}>
                    School Name
                  </label>
                  <input
                    type="text"
                    name="school_name"
                    value={form.school_name}
                    onChange={handleChange}
                    data-testid="contact-input-school"
                    className="w-full bg-white border-2 border-gray-200 text-[#0A0A0A] p-3.5 focus:ring-2 focus:ring-[#0A0A0A] focus:border-[#0A0A0A] outline-none text-sm font-medium transition-all"
                    style={{ fontFamily: "'Satoshi', sans-serif" }}
                    placeholder="Accra Academy"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2 font-black" style={{ fontFamily: "'Satoshi', sans-serif" }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    data-testid="contact-input-phone"
                    className="w-full bg-white border-2 border-gray-200 text-[#0A0A0A] p-3.5 focus:ring-2 focus:ring-[#0A0A0A] focus:border-[#0A0A0A] outline-none text-sm font-medium transition-all"
                    style={{ fontFamily: "'Satoshi', sans-serif" }}
                    placeholder="+233 XX XXX XXXX"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2 font-black" style={{ fontFamily: "'Satoshi', sans-serif" }}>
                  Tell Us About Your School *
                </label>
                <textarea
                  name="message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={handleChange}
                  data-testid="contact-input-message"
                  className="w-full bg-white border-2 border-gray-200 text-[#0A0A0A] p-3.5 focus:ring-2 focus:ring-[#0A0A0A] focus:border-[#0A0A0A] outline-none text-sm resize-none font-medium transition-all"
                  style={{ fontFamily: "'Satoshi', sans-serif" }}
                  placeholder="How many students? What challenges are you facing? What modules do you need?"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="w-full bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white font-black py-4 text-sm uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ fontFamily: "'Satoshi', sans-serif" }}
              >
                {loading ? "Sending..." : "Request Custom Proposal"}
                {!loading && <Send size={16} strokeWidth={3} />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
